import React, { memo, useContext } from 'react'
import { GENERIC_PURECARS_DOMAIN } from '@signal/utils/constants'
import { setCookie } from '@signal/utils/cookies'
/* webpack-strip-block:removed */
import SWRRootContext from '../swr/RootContext'
import { useSWRWithCache } from '../swr/hooks'
import { ContextFetchHookType, ContextFetchLoaderPropsType } from './types'
import { createFetchContext, createFetchContextProps } from './FetchContext'

/**
 * @component ContextFetcher
 * @template <FetchDataType> - the data type handled from this context fetcher
 * @template <FetchHookParamsType> - the parameters that need to be sent to the context fetch hook function
 * The <ContextFetcher> React component which handles a single context fetch
 */
export function ContextFetcher<FetchDataType, FetchHookParamsType>({
  children,
  fetchHook = useSWRWithCache as ContextFetchHookType<
    FetchDataType,
    FetchHookParamsType
  >,
  FetchContext = createFetchContext<FetchDataType>(),
  // any remaining properties will be sent to the hook function
  ...hookParams
}: ContextFetchLoaderPropsType<
  FetchDataType,
  FetchHookParamsType
>): JSX.Element {
  const { rethrowContextFetchErrors } = useContext(SWRRootContext)
  const fetchContext = createFetchContextProps<FetchDataType>({
    isFetching: true
  })

  try {
    const result = fetchHook((hookParams as unknown) as FetchHookParamsType)
    if (result) {
      fetchContext.isFetching = false
      fetchContext.sawError = false
      fetchContext.response = result
    }
  } catch (error) {
    fetchContext.sawError = true

    /* webpack-strip-block:removed */

    if (rethrowContextFetchErrors) {
      throw error
    }
  }

  return (
    <FetchContext.Provider value={fetchContext}>
      {children}
    </FetchContext.Provider>
  )
}

// note - do not use this in typescript, use castFetchContext instead
export default memo(ContextFetcher)
